/**
 * Three Plume 3D Visualization Component
 *
 * Renders a 3D cross-sectional view of the three-plume model showing:
 * - Geological layers (soil surface, vadose zone, water table, aquifer)
 * - PSH/LNAPL at water table
 * - Dissolved plume in saturated zone
 * - Vapor plume in vadose zone
 * - Recovery and MDPE wells
 *
 * Syncs with time slider and well placements from the 2D map view.
 */

import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { Canvas, useFrame, useThree, RootState } from '@react-three/fiber';
import { OrbitControls, Text, Line } from '@react-three/drei';
import * as THREE from 'three';
import {
  ThreePlumeState,
  RecoveryWell,
  MDPEWell,
  SiteGradientMap,
  GradientContour,
  ContaminantBoundaryData,
  COMPOUND_PARAMETERS,
  FlowArrow,
} from '../../api/polygon';
import { PSHVoxelCube, usePSHData, PSHMetadata } from './PSHVoxelCube';
import { getCompoundProperties } from './MultiContaminantPlumeUtils';

// ============================================================================
// FOCUSED INSPECTION TYPES
// ============================================================================

type FocusedPlumeType = 'psh' | 'dissolved' | 'vapor' | null;

// Multi-stage inspection flow
type InspectionStage =
  | 'overview'           // Full 3D scene
  | 'dissolved-focus'    // Zoomed into dissolved plume group
  | 'pancake-stack'      // Contaminants separated vertically
  | 'single-plume'       // Single contaminant isolated
  | 'psh-focus';         // Zoomed into PSH with VE magnifier slider

interface InspectionState {
  stage: InspectionStage;
  focusedPlume: FocusedPlumeType;
  selectedCompound: string | null;  // For single-plume stage
}

/**
 * Get compound color using shared getCompoundProperties (single source of truth).
 * Uses alias + substring matching from MultiContaminantPlumeUtils — identical to 2D map.
 */
function getCompoundColorFromParams(compoundName: string): string {
  return getCompoundProperties(compoundName).plume_color;
}

// Compound display names and tailwind classes for UI
const COMPOUND_COLORS: Record<string, { hex: string; tailwind: string; name: string }> = {
  'total btex': { hex: '#06b6d4', tailwind: 'text-cyan-500', name: 'Total BTEX' },
  'total tph': { hex: '#6b7280', tailwind: 'text-gray-500', name: 'Total TPH' },
  benzene: { hex: '#3b82f6', tailwind: 'text-blue-500', name: 'Benzene' },
  toluene: { hex: '#22c55e', tailwind: 'text-green-500', name: 'Toluene' },
  ethylbenzene: { hex: '#f59e0b', tailwind: 'text-amber-500', name: 'Ethylbenzene' },
  xylenes: { hex: '#a855f7', tailwind: 'text-purple-500', name: 'Xylenes' },
  'c6-c12': { hex: '#f97316', tailwind: 'text-orange-500', name: 'C6-C12' },
  'c12-c28': { hex: '#854d0e', tailwind: 'text-amber-800', name: 'C12-C28' },
  'c28-c35': { hex: '#78716c', tailwind: 'text-stone-500', name: 'C28-C35' },
};

// ============================================================================
// TYPES
// ============================================================================

export interface LayerVisibility {
  psh: boolean;
  dissolved: boolean;
  vapor: boolean;
}

// Monitoring well marker type (display only)
export interface MonitoringWellMarker {
  id: string;
  well_id: string;
  latitude: number;
  longitude: number;
  depth_ft?: number;
}

export interface ThreePlume3DViewProps {
  plumeState: ThreePlumeState;
  depthToWaterFt: number;
  aquiferThicknessFt: number;
  flowDirection: number;
  recoveryWells?: RecoveryWell[];
  mdpeWells?: MDPEWell[];
  monitoringWells?: MonitoringWellMarker[];  // Monitoring wells (display only)
  sourceLatLon?: { lat: number; lon: number };
  visibleLayers?: LayerVisibility;
  // Peak/initial values for concentration gradient visualization
  initialPshMassLbs?: number;
  peakDissolvedConcUgL?: number;
  peakVaporConcUgM3?: number;
  // Gradient map for water table contours
  gradientMap?: SiteGradientMap | null;
  showGradientContours?: boolean;
  // Multi-contaminant plume support (BTEX compounds)
  multiContaminantBoundaries?: ContaminantBoundaryData[];
  visibleCompounds?: Set<string>;
  // Wells with concentration data for particle rendering
  wellsWithConcentrations?: Array<{
    well_id: string;
    lat: number;
    lon: number;
    concentration: number;
    compound?: string;
  }>;
  // v3.2 per-(well_name, analyte_code_lower) → predicted_conc_mgl override at
  // the user-selected horizon. When non-empty AND horizonDays > 0, the 3D
  // gradient swaps each well's measured concentration for the forecast value.
  wellHorizonOverrides?: Map<string, Map<string, number>>;
  horizonDays?: number;
  // Site ID (informational)
  siteId?: string;
  // PSH well data (fetched by parent to avoid duplicate API calls)
  pshTargetDate?: string | null;
  // Pre-computed PSH wells from snapshot (skip API call when available)
  pshWellsFromSnapshot?: Array<{ well_id: string; lat: number; lon: number; product_thickness_ft: number; depth_to_water_ft?: number; is_inferred?: boolean }> | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Scale: 1 unit = 10 feet for reasonable scene size
const SCALE = 0.1;

// Bright, vibrant colors for 3D visibility
const COLORS = {
  // PSH/LNAPL - Bright Orange/Amber
  psh: {
    core: '#f59e0b',
    outer: '#fcd34d',
  },
  // Dissolved - Bright Cyan/Blue
  dissolved: {
    core: '#06b6d4',
    outer: '#67e8f9',
  },
  // Vapor - Bright Magenta/Purple
  vapor: {
    core: '#d946ef',
    outer: '#f0abfc',
  },
  // Geological - Brighter tones
  surface: '#a8896c',
  vadoseZone: '#e8c9a0',
  waterTable: '#60a5fa',
  aquifer: '#7dd3fc',
  bedrock: '#6b7280',
  // Wells - Monochrome
  recoveryWell: '#a3a3a3',
  mdpeWell: '#737373',
  // Source - Bright red
  source: '#f43f5e',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function areaToRadius(areaSqft: number): number {
  return Math.sqrt(areaSqft / Math.PI);
}

/**
 * Smooth boundary points using Catmull-Rom spline interpolation.
 * This creates organic, flowing curves instead of sharp angular boundaries.
 *
 * @param points - Array of {x, z} points (2D boundary in XZ plane)
 * @param iterations - Number of subdivision iterations (each doubles point count)
 * @param tension - Spline tension (0 = loose curves, 1 = tight curves)
 * @returns Smoothed array of points
 */
function smoothBoundaryPoints(
  points: Array<{ x: number; z: number }>,
  iterations: number = 2,
  tension: number = 0.5
): Array<{ x: number; z: number }> {
  if (points.length < 3) return points;

  let smoothed = [...points];

  for (let iter = 0; iter < iterations; iter++) {
    const newPoints: Array<{ x: number; z: number }> = [];
    const n = smoothed.length;

    for (let i = 0; i < n; i++) {
      // Get 4 control points for Catmull-Rom (wrapping around for closed curve)
      const p0 = smoothed[(i - 1 + n) % n];
      const p1 = smoothed[i];
      const p2 = smoothed[(i + 1) % n];
      const p3 = smoothed[(i + 2) % n];

      // Add original point
      newPoints.push(p1);

      // Add midpoint using Catmull-Rom interpolation at t=0.5
      const t = 0.5;
      const t2 = t * t;
      const t3 = t2 * t;

      // Catmull-Rom basis functions with tension
      const s = (1 - tension) / 2;
      const b0 = -s * t3 + 2 * s * t2 - s * t;
      const b1 = (2 - s) * t3 + (s - 3) * t2 + 1;
      const b2 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
      const b3 = s * t3 - s * t2;

      newPoints.push({
        x: b0 * p0.x + b1 * p1.x + b2 * p2.x + b3 * p3.x,
        z: b0 * p0.z + b1 * p1.z + b2 * p2.z + b3 * p3.z,
      });
    }

    smoothed = newPoints;
  }

  return smoothed;
}

/**
 * Convert Vector3 array to XZ points and apply smoothing
 */
function smoothVector3Boundary(
  points: THREE.Vector3[],
  iterations: number = 2,
  tension: number = 0.5
): THREE.Vector3[] {
  if (points.length < 3) return points;

  // Convert to 2D (XZ plane)
  const points2D = points.map(p => ({ x: p.x, z: p.z }));

  // Apply smoothing
  const smoothed2D = smoothBoundaryPoints(points2D, iterations, tension);

  // Convert back to Vector3 (preserve Y from first point)
  const y = points[0].y;
  return smoothed2D.map(p => new THREE.Vector3(p.x, y, p.z));
}

/**
 * Smooth lat/lon boundary points using Catmull-Rom spline interpolation.
 * Used for voxel grid polygon boundaries.
 */
/**
 * Chaikin corner-cutting smoothing — same algorithm as 2D PlumeVoxelCanvasLayer.
 * Ensures 3D plume boundaries match 2D exactly.
 */
function smoothLatLonBoundary(
  points: [number, number][],
  iterations: number = 4,
  _tension?: number
): [number, number][] {
  if (points.length < 3) return points;

  let smoothed = [...points];

  for (let iter = 0; iter < iterations; iter++) {
    const newPoints: [number, number][] = [];
    const n = smoothed.length;

    for (let i = 0; i < n; i++) {
      const p1 = smoothed[i];
      const p2 = smoothed[(i + 1) % n];

      // Chaikin: cut corners at 25% and 75% of each edge
      newPoints.push([
        0.75 * p1[0] + 0.25 * p2[0],
        0.75 * p1[1] + 0.25 * p2[1],
      ]);
      newPoints.push([
        0.25 * p1[0] + 0.75 * p2[0],
        0.25 * p1[1] + 0.75 * p2[1],
      ]);
    }

    smoothed = newPoints;
  }

  return smoothed;
}

// ============================================================================
// CAMERA CONTROLLER FOR MULTI-STAGE INSPECTION
// ============================================================================

interface CameraControllerProps {
  inspectionState: InspectionState;
  plumePositions: {
    psh: THREE.Vector3;
    dissolved: THREE.Vector3;
    vapor: THREE.Vector3;
  };
  compoundPositions?: Map<string, THREE.Vector3>;  // For single-plume targeting
  defaultPosition: THREE.Vector3;
  defaultTarget: THREE.Vector3;
  orbitControlsRef: React.RefObject<any>;
}

function CameraController({
  inspectionState,
  plumePositions,
  compoundPositions,
  defaultPosition,
  defaultTarget,
  orbitControlsRef,
}: CameraControllerProps) {
  const { camera, invalidate } = useThree();
  const isAnimatingRef = useRef(false);
  const prevStateRef = useRef<string>('overview');
  const targetPosRef = useRef<THREE.Vector3 | null>(null);
  const targetLookAtRef = useRef<THREE.Vector3 | null>(null);

  // Create a unique state key for comparison
  const stateKey = `${inspectionState.stage}-${inspectionState.focusedPlume}-${inspectionState.selectedCompound}`;

  useFrame((_state: unknown, delta: number) => {
    if (!orbitControlsRef.current) return;

    // Detect state change
    if (prevStateRef.current !== stateKey) {
      prevStateRef.current = stateKey;
      isAnimatingRef.current = true;

      // Calculate camera targets based on inspection stage
      switch (inspectionState.stage) {
        case 'overview':
          targetPosRef.current = defaultPosition.clone();
          targetLookAtRef.current = defaultTarget.clone();
          break;

        case 'dissolved-focus':
          // Zoom to dissolved plume group
          const dissolvedPos = plumePositions.dissolved;
          targetLookAtRef.current = dissolvedPos.clone();
          targetPosRef.current = new THREE.Vector3(
            dissolvedPos.x + 8,
            dissolvedPos.y + 5,
            dissolvedPos.z + 8
          );
          break;

        case 'pancake-stack':
          // Side view to see the stack
          const stackCenter = plumePositions.dissolved.clone();
          stackCenter.y -= 2;  // Center on the stack
          targetLookAtRef.current = stackCenter;
          targetPosRef.current = new THREE.Vector3(
            12,  // More to the side
            stackCenter.y + 2,
            3    // Slightly angled
          );
          break;

        case 'single-plume':
          // Zoom into the selected compound
          if (inspectionState.selectedCompound && compoundPositions) {
            const compoundPos = compoundPositions.get(inspectionState.selectedCompound);
            if (compoundPos) {
              targetLookAtRef.current = compoundPos.clone();
              targetPosRef.current = new THREE.Vector3(
                compoundPos.x + 5,
                compoundPos.y + 3,
                compoundPos.z + 5
              );
            }
          }
          break;

        case 'psh-focus': {
          const pshPos = plumePositions.psh;
          targetLookAtRef.current = pshPos.clone();
          targetPosRef.current = new THREE.Vector3(
            pshPos.x + 4,
            pshPos.y + 3,
            pshPos.z + 4
          );
          break;
        }

        default:
          // Vapor focus (legacy behavior)
          if (inspectionState.focusedPlume && inspectionState.focusedPlume !== 'dissolved' && inspectionState.focusedPlume !== 'psh') {
            const plumePos = plumePositions[inspectionState.focusedPlume];
            targetLookAtRef.current = plumePos.clone();
            const distance = inspectionState.focusedPlume === 'vapor' ? 6 : 5;
            targetPosRef.current = new THREE.Vector3(
              plumePos.x + distance,
              plumePos.y + distance * 0.6,
              plumePos.z + distance
            );
          }
      }

      // Disable controls during animation
      orbitControlsRef.current.enabled = false;
    }

    // If not animating, do nothing - let OrbitControls handle everything
    if (!isAnimatingRef.current || !targetPosRef.current || !targetLookAtRef.current) {
      return;
    }

    // Animate toward target
    const posDistance = camera.position.distanceTo(targetPosRef.current);
    const targetDistance = orbitControlsRef.current.target.distanceTo(targetLookAtRef.current);

    if (posDistance > 0.1 || targetDistance > 0.1) {
      const lerpFactor = 1 - Math.pow(0.02, delta);
      camera.position.lerp(targetPosRef.current, lerpFactor);
      orbitControlsRef.current.target.lerp(targetLookAtRef.current, lerpFactor);
      orbitControlsRef.current.update();
      invalidate();
    } else {
      // Animation complete - re-enable controls
      isAnimatingRef.current = false;
      orbitControlsRef.current.enabled = true;
    }
  });

  return null;
}

// ============================================================================
// 3D COMPONENTS
// ============================================================================

// Ground surface layer - rendered as very subtle hint
const GroundSurface = React.memo(function GroundSurface({ size }: { size: number }) {
  return (
    <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[size, size]} />
      <meshStandardMaterial
        color={COLORS.surface}
        transparent
        opacity={0.15}
        side={THREE.DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
});

// Water table plane (simple fallback) - returns null, no visible plane
function WaterTable({ size, depth }: { size: number; depth: number }) {
  return null;
}

// ── Gauging-well coverage hull (scene X/Z) — solid contours inside, dashed out.
type XZ = [number, number];

function hullXZ(pts: XZ[]): XZ[] {
  const p = pts.filter(q => Number.isFinite(q[0]) && Number.isFinite(q[1]))
    .sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  if (p.length < 3) return p;
  const cross = (o: XZ, a: XZ, b: XZ) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: XZ[] = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop(); lower.push(q); }
  const upper: XZ[] = [];
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop(); upper.push(q); }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

function pointInPolyXZ(x: number, z: number, poly: XZ[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], zi = poly[i][1], xj = poly[j][0], zj = poly[j][1];
    if (((zi > z) !== (zj > z)) && (x < ((xj - xi) * (z - zi)) / (zj - zi) + xi)) inside = !inside;
  }
  return inside;
}

/** Split a Vector3 contour into inside/outside-hull runs (segment-midpoint test). */
function splitVec3ByHull(points: THREE.Vector3[], hull: XZ[]): { points: THREE.Vector3[]; inside: boolean }[] {
  if (hull.length < 3 || points.length < 2) return [{ points, inside: true }];
  const runs: { points: THREE.Vector3[]; inside: boolean }[] = [];
  let cur: THREE.Vector3[] = [points[0]];
  let curInside: boolean | null = null;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    const segInside = pointInPolyXZ((a.x + b.x) / 2, (a.z + b.z) / 2, hull);
    if (curInside === null) curInside = segInside;
    if (segInside !== curInside) { runs.push({ points: cur, inside: curInside }); cur = [a]; curInside = segInside; }
    cur.push(b);
  }
  if (cur.length >= 2) runs.push({ points: cur, inside: curInside ?? true });
  return runs;
}

// Water table with gradient contours - shows elevation variation
const WaterTableWithContours = React.memo(function WaterTableWithContours({
  size,
  depth,
  contours,
  sourceLatLon,
  showContours = true,
  flowArrows,
  gradientWells,
}: {
  size: number;
  depth: number;
  contours: GradientContour[];
  sourceLatLon: { lat: number; lon: number };
  showContours?: boolean;
  flowArrows?: FlowArrow[];
  gradientWells?: { lat: number; lon: number }[];
}) {
  // Convert contour coordinates to 3D scene coordinates
  const contourLines = useMemo(() => {
    if (!contours || contours.length === 0 || !showContours) return [];

    const sourceLat = sourceLatLon.lat;
    const sourceLon = sourceLatLon.lon;

    // Get min/max elevations for color scaling
    const elevations = contours
      .map(c => c.elevation_ft)
      .filter((e): e is number => e !== null && e !== undefined);
    const minElev = elevations.length > 0 ? Math.min(...elevations) : 0;
    const maxElev = elevations.length > 0 ? Math.max(...elevations) : 100;
    const elevRange = maxElev - minElev || 1;

    // lat/lon -> scene X/Z (same transform as the contour points below)
    const toXZ = (lat: number, lon: number): XZ => {
      const eastFt = (lon - sourceLon) * 364000 * Math.cos(sourceLat * Math.PI / 180);
      const northFt = (lat - sourceLat) * 364000;
      return [
        Math.max(-size / 2 * 10, Math.min(size / 2 * 10, eastFt * SCALE)),
        Math.max(-size / 2 * 10, Math.min(size / 2 * 10, -northFt * SCALE)),
      ];
    };
    // Convex hull of the gauging wells = data-control boundary (scene X/Z).
    const wellHull = hullXZ((gradientWells || [])
      .filter(w => Number.isFinite(w.lat) && Number.isFinite(w.lon))
      .map(w => toXZ(w.lat, w.lon)));

    return contours
      .filter(c => c.elevation_ft !== null && c.coordinates.length >= 2)
      .map((contour, idx) => {
        // Convert lat/lon to 3D scene coordinates
        // 3D coordinate system: X = East (+), Z = South (+), so North = -Z
        const points: THREE.Vector3[] = contour.coordinates.map(([lat, lon]) => {
          const [x, z] = toXZ(lat, lon);
          return new THREE.Vector3(x, 0, z);
        });

        // Color based on elevation (blue gradient - darker = lower, lighter = higher)
        const normalizedElev = (contour.elevation_ft! - minElev) / elevRange;
        const hue = 200 + normalizedElev * 20; // Blue range 200-220
        const saturation = 70 + normalizedElev * 20; // More saturated for higher
        const lightness = 30 + normalizedElev * 40; // Lighter for higher elevation
        const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

        // Determine if major contour (every 5 ft)
        const isMajor = contour.elevation_ft !== null && Math.abs(contour.elevation_ft % 5) < 0.1;

        return {
          points,
          runs: splitVec3ByHull(points, wellHull),  // solid inside hull, dashed outside
          color,
          elevation: contour.elevation_ft,
          isMajor,
          key: `contour-${idx}-${contour.elevation_ft}`,
        };
      });
  }, [contours, sourceLatLon, size, showContours, gradientWells]);

  // Calculate average water table depth offset from contours
  const avgElevOffset = useMemo(() => {
    const elevations = contours
      .map(c => c.elevation_ft)
      .filter((e): e is number => e !== null);
    if (elevations.length === 0) return 0;
    const avgElev = elevations.reduce((a, b) => a + b, 0) / elevations.length;
    // Normalize offset for visualization (scale down significantly)
    return (avgElev - elevations[0]) * 0.01;
  }, [contours]);

  const yPos = -depth * SCALE;

  // Memoize static grid line points so they aren't recreated on every render
  const gridLines = useMemo(() => {
    const coords = [-8, -4, 0, 4, 8];
    const xLines = coords.map(x => ({
      key: `grid-x-${x}`,
      points: [
        new THREE.Vector3(x, 0.01, -size / 2),
        new THREE.Vector3(x, 0.01, size / 2),
      ] as [THREE.Vector3, THREE.Vector3],
    }));
    const zLines = coords.map(z => ({
      key: `grid-z-${z}`,
      points: [
        new THREE.Vector3(-size / 2, 0.01, z),
        new THREE.Vector3(size / 2, 0.01, z),
      ] as [THREE.Vector3, THREE.Vector3],
    }));
    return { xLines, zLines };
  }, [size]);

  // Small flow direction arrows at well locations
  const flowArrowMeshes = useMemo(() => {
    if (!flowArrows || flowArrows.length === 0 || !showContours) return [];

    const sourceLat = sourceLatLon.lat;
    const sourceLon = sourceLatLon.lon;

    return flowArrows
      .filter(a => a.lat != null && a.lon != null && a.direction_deg != null)
      .map((arrow, idx) => {
        const eastFt = (arrow.lon - sourceLon) * 364000 * Math.cos(sourceLat * Math.PI / 180);
        const northFt = (arrow.lat - sourceLat) * 364000;
        const x = Math.max(-size / 2 * 10, Math.min(size / 2 * 10, eastFt * SCALE));
        const z = Math.max(-size / 2 * 10, Math.min(size / 2 * 10, -northFt * SCALE));

        // Compass bearing → Three.js Y rotation
        const bearingRad = (arrow.direction_deg! * Math.PI) / 180;

        return { x, z, bearingRad, key: `flow-arrow-${idx}` };
      });
  }, [flowArrows, sourceLatLon, size, showContours]);

  return (
    <group position={[0, yPos, 0]}>
      {/* Water table plane removed - only show contour lines */}

      {/* Gradient contour lines on top of water table — solid inside the
          gauging-well hull (interpolated), dashed outside (extrapolated). */}
      {contourLines.map(({ points, runs, color, elevation, isMajor, key }) => (
        <group key={key}>
          {runs.map((run, runIdx) => (
            <Line
              key={`${key}-${runIdx}`}
              points={run.points}
              color={color}
              lineWidth={isMajor ? 3 : 1.5}
              transparent
              opacity={isMajor ? 0.9 : 0.7}
              dashed={!run.inside}
              dashSize={1.2}
              gapSize={0.8}
            />
          ))}
          {/* Elevation labels on major contours */}
          {isMajor && points.length > 2 && elevation !== null && (
            <Text
              position={[
                points[Math.floor(points.length / 2)].x,
                0.15,
                points[Math.floor(points.length / 2)].z,
              ]}
              fontSize={0.25}
              color={color}
              anchorX="center"
              anchorY="middle"
              rotation={[-Math.PI / 2, 0, 0]}
            >
              {elevation.toFixed(1)}′
            </Text>
          )}
        </group>
      ))}

      {/* Small flow direction cones at well locations */}
      {flowArrowMeshes.map(({ x, z, bearingRad, key }) => (
        <group key={key} position={[x, 0.06, z]} rotation={[0, -bearingRad, 0]}>
          {/* Small cone offset from well, pointing in flow direction */}
          <mesh position={[0, 0, -0.35]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.08, 0.22, 5]} />
            <meshStandardMaterial color="#2563eb" transparent opacity={0.85} />
          </mesh>
        </group>
      ))}

      {/* Grid lines for reference when no contours */}
      {(!showContours || contourLines.length === 0) && (
        <>
          {gridLines.xLines.map(({ key, points: pts }) => (
            <Line
              key={key}
              points={pts}
              color="#3b82f6"
              lineWidth={0.5}
              transparent
              opacity={0.3}
            />
          ))}
          {gridLines.zLines.map(({ key, points: pts }) => (
            <Line
              key={key}
              points={pts}
              color="#3b82f6"
              lineWidth={0.5}
              transparent
              opacity={0.3}
            />
          ))}
        </>
      )}
    </group>
  );
});

// Aquifer bottom
const AquiferBottom = React.memo(function AquiferBottom({ size, depth, thickness }: { size: number; depth: number; thickness: number }) {
  return (
    <mesh position={[0, -(depth + thickness) * SCALE, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[size, size]} />
      <meshStandardMaterial
        color={COLORS.bedrock}
        transparent
        opacity={0.5}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
});

// Generate distorted circle boundary for PSH with MDPE well extraction effect
// EXACT COPY of 2D generateDistortedCircle logic - compute in lat/lon then convert to 3D
function generateDistortedPSHBoundary(
  radiusFt: number,
  wells: MDPEWell[],
  sourceLatLon: { lat: number; lon: number },
  scaleFactor: number = 1.0,
  numPoints: number = 24
): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];
  const centerLat = sourceLatLon.lat;
  const centerLon = sourceLatLon.lon;

  const feetToLatDegrees = (ft: number) => ft / 364000;
  const feetToLonDegrees = (ft: number, lat: number) => ft / (364000 * Math.cos(lat * Math.PI / 180));

  const minRadiusFt = radiusFt * 0.2;
  const scaledRadiusFt = radiusFt * scaleFactor;

  for (let i = 0; i <= numPoints; i++) {
    const theta = (2 * Math.PI * i) / numPoints;

    // Calculate effective radius at this angle after all well effects
    let effectiveRadius = scaledRadiusFt;

    // Apply MDPE extraction effect - PSH contracts toward the well
    // This shows mass being extracted/volatilized at the well location
    for (const well of wells) {
      if (!well.latitude || !well.longitude) continue;

      // Distance from well to PSH center (in feet)
      const wellDLatFt = (well.latitude - centerLat) * 364000;
      const wellDLonFt = (well.longitude - centerLon) * 364000 * Math.cos(centerLat * Math.PI / 180);
      const wellDistFromCenterFt = Math.sqrt(wellDLatFt * wellDLatFt + wellDLonFt * wellDLonFt);

      // Angle from center to well
      const wellAngle = Math.atan2(wellDLatFt, wellDLonFt);

      // Angular distance between this point and the well direction
      let angleDiff = Math.abs(theta - wellAngle);
      if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;

      // ROI calculation matching backend physics:
      // Backend: roi_ft = 30 + (Q_air_ft3_day / 1000) ** 0.5 * 10
      // CFM to ft³/day: cfm * 60 * 24 = cfm * 1440
      const ft3PerDay = well.extraction_cfm * 1440;
      const roiFt = Math.min(30 + Math.sqrt(ft3PerDay / 1000) * 10, 100);

      // Effect is strongest when looking toward the well (angleDiff near 0)
      // and falls off smoothly at larger angles
      const angularInfluence = Math.max(0, Math.cos(angleDiff * 1.5));

      if (angularInfluence > 0) {
        // CFM scaling (higher extraction = more mass removal = more contraction)
        const cfmFactor = Math.min(Math.sqrt(well.extraction_cfm / 50), 2.0);

        // Distance factor: wells closer to the PSH boundary have more effect
        const distFactor = wellDistFromCenterFt < scaledRadiusFt
          ? 1.0  // Well inside PSH: full effect
          : Math.max(0, 1 - (wellDistFromCenterFt - scaledRadiusFt) / roiFt);

        // Total contraction: max 40% radius reduction per well, smooth angular falloff
        const contractionRatio = angularInfluence * cfmFactor * distFactor * 0.4;

        // Apply radius reduction (multiplicative to prevent over-contraction)
        effectiveRadius *= (1 - contractionRatio);
      }
    }

    // Enforce minimum radius to prevent topology collapse
    effectiveRadius = Math.max(effectiveRadius, minRadiusFt * scaleFactor);

    // Generate point at effective radius
    const lat = centerLat + feetToLatDegrees(effectiveRadius * Math.sin(theta));
    const lon = centerLon + feetToLonDegrees(effectiveRadius * Math.cos(theta), centerLat);

    // Convert from lat/lon to 3D coordinates (same as dissolved plume)
    const eastFt = (lon - centerLon) * 364000 * Math.cos(centerLat * Math.PI / 180);
    const northFt = (lat - centerLat) * 364000;

    // Map geographic to 3D: East -> +X, North -> -Z (standard 3D convention)
    const x = eastFt * SCALE;
    const z = -northFt * SCALE;

    points.push(new THREE.Vector3(x, 0, z));
  }

  return points;
}

// PSH Plume - 3D lens/disk floating ON TOP of water table (LNAPL is lighter than water)
// Shows concentration gradient with multiple layers (core = high conc, outer = low conc)
// Distorts toward MDPE wells (matching 2D behavior)
function PSHPlume({
  areaSqft,
  thicknessFt,
  depthToWaterFt,
  massLbs,
  initialMassLbs,
  mdpeWells = [],
  sourceLatLon,
}: {
  areaSqft: number;
  thicknessFt: number;
  depthToWaterFt: number;
  massLbs?: number;
  initialMassLbs?: number;
  mdpeWells?: MDPEWell[];
  sourceLatLon: { lat: number; lon: number };
}) {
  const radiusFt = areaToRadius(areaSqft);

  if (areaSqft <= 0) return null;

  // PSH floats ON TOP of water table (LNAPL is less dense than water)
  // Bottom of PSH touches water table, extends upward into vadose zone
  const yPos = -depthToWaterFt * SCALE + (thicknessFt * SCALE) / 2;

  // Calculate concentration factor based on mass depletion
  const concFactor = initialMassLbs && massLbs ? Math.max(0.1, massLbs / initialMassLbs) : 1;

  // Core opacity reflects concentration - depleted PSH is more transparent
  const coreOpacity = 0.5 + concFactor * 0.4; // 0.5 to 0.9

  // Generate distorted boundary shapes for each concentration layer
  const boundaryShapes = useMemo(() => {
    if (radiusFt <= 0) return null;

    // Concentration layers from outer to inner (matching dissolved plume pattern)
    const layers = [
      { scale: 1.15, color: COLORS.psh.outer, opacity: 0.15, name: 'halo', heightScale: 0.6 },
      { scale: 1.0, color: COLORS.psh.outer, opacity: coreOpacity * 0.4, name: 'outer', heightScale: 0.8 },
      { scale: 0.75, color: COLORS.psh.core, opacity: coreOpacity * 0.7, name: 'mid', heightScale: 1.0 },
      { scale: 0.5, color: '#b45309', opacity: coreOpacity, name: 'core', heightScale: 1.2 },
    ];

    return layers.map(layer => ({
      ...layer,
      points: generateDistortedPSHBoundary(
        radiusFt,
        mdpeWells || [],
        sourceLatLon,
        layer.scale,
        32  // Sufficient points for smooth curves in 3D
      )
    }));
  }, [radiusFt, mdpeWells, sourceLatLon, coreOpacity]);

  const verticalExtent = thicknessFt * SCALE;

  // Pre-compute geometries to avoid recreating every render
  const layerGeometries = useMemo(() => {
    if (!boundaryShapes) return null;
    return boundaryShapes.map((layer, idx) => {
      const shape = new THREE.Shape();
      const smoothedPoints = smoothVector3Boundary(layer.points, 1, 0.5);
      if (smoothedPoints.length > 0) {
        shape.moveTo(smoothedPoints[0].x, smoothedPoints[0].z);
        for (let i = 1; i < smoothedPoints.length; i++) {
          shape.lineTo(smoothedPoints[i].x, smoothedPoints[i].z);
        }
        shape.closePath();
      }
      const extrudeSettings = {
        steps: 1,
        depth: verticalExtent * layer.heightScale,
        bevelEnabled: false,
      };
      return { ...layer, geometry: new THREE.ExtrudeGeometry(shape, extrudeSettings) };
    });
  }, [boundaryShapes, verticalExtent]);

  if (!layerGeometries) return null;

  return (
    <group position={[0, yPos, 0]}>
      {layerGeometries.map((layer, idx) => (
        <mesh
          key={`psh-layer-${layer.name}`}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, -verticalExtent * layer.heightScale / 2 + idx * 0.01, 0]}
          geometry={layer.geometry}
        >
          <meshStandardMaterial
            color={layer.color}
            transparent
            opacity={layer.opacity}
            emissive={layer.color}
            emissiveIntensity={layer.name === 'core' ? 0.5 : layer.name === 'mid' ? 0.4 : 0.2}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

// Dissolved Plume - Elongated ellipsoid below water table
function DissolvedPlume({
  lengthFt,
  widthFt,
  depthToWaterFt,
  flowDirection,
}: {
  lengthFt: number;
  widthFt: number;
  depthToWaterFt: number;
  flowDirection: number;
}) {
  if (lengthFt <= 0 || widthFt <= 0) return null;

  // Plume extends below water table, center offset downgradient
  const migrationOffset = lengthFt * 0.25 * SCALE;
  const plumeDepth = 15; // Assume 15ft plume thickness in aquifer

  // Convert flow direction to radians for rotation
  const flowRad = (flowDirection - 90) * Math.PI / 180;

  // Position: center offset downgradient, below water table
  const xOffset = migrationOffset * Math.cos((90 - flowDirection) * Math.PI / 180);
  const zOffset = -migrationOffset * Math.sin((90 - flowDirection) * Math.PI / 180);
  const yPos = -(depthToWaterFt + plumeDepth / 2) * SCALE;

  return (
    <mesh
      position={[xOffset, yPos, zOffset]}
      rotation={[0, -flowRad, 0]}
    >
      <sphereGeometry args={[1, 16, 16]} />
      <meshStandardMaterial
        color={COLORS.dissolved.core}
        transparent
        opacity={0.5}
      />
      {/* Scale to ellipsoid shape */}
      <primitive object={new THREE.Object3D()} scale={[
        (lengthFt / 2) * SCALE,
        plumeDepth * SCALE,
        (widthFt / 2) * SCALE
      ]} />
    </mesh>
  );
}

// Generate distorted ellipse boundary points with recovery well SINK effect
// WORLD COORDINATE SYSTEM for 3D view (matches 2D map):
// - X axis = East (positive = east, right on map)
// - Z axis = South (positive = south, down on map)
// - This matches the 2D map where North is up
//
// Wells have latitude/longitude which are converted to eastFt/southFt
function generateDistortedEllipseBoundary(
  lengthFt: number,
  widthFt: number,
  migrationFt: number,
  flowDirection: number,
  wells: RecoveryWell[],
  sourceLatLon: { lat: number; lon: number },
  concentrationLevel: number = 1.0,
  numPoints: number = 24
): THREE.Vector3[] {
  // =======================================================================
  // EXACT COPY of 2D generateDistortedMigratedEllipse - compute in lat/lon
  // then convert to 3D at the very end
  // =======================================================================

  const points: THREE.Vector3[] = [];
  const sourceLat = sourceLatLon.lat;
  const sourceLon = sourceLatLon.lon;

  // Helper functions (same as 2D)
  const feetToLatDegrees = (ft: number) => ft / 364000;
  const feetToLonDegrees = (ft: number, lat: number) => ft / (364000 * Math.cos(lat * Math.PI / 180));

  // STEP 1: Calculate plume center (migrated downgradient from source) - SAME AS 2D
  const flowRadians = (90 - flowDirection) * Math.PI / 180;
  const offsetEastFt = migrationFt * Math.cos(flowRadians);
  const offsetNorthFt = migrationFt * Math.sin(flowRadians);

  const plumeCenterLat = sourceLat + feetToLatDegrees(offsetNorthFt);
  const plumeCenterLon = sourceLon + feetToLonDegrees(offsetEastFt, sourceLat);



  // STEP 2: Generate base ellipse coordinates - SAME AS 2D
  const angleRad = (flowDirection - 90) * Math.PI / 180;

  for (let i = 0; i <= numPoints; i++) {
    const theta = (2 * Math.PI * i) / numPoints;

    // Ellipse in local coordinates (centered at origin)
    const localX = (lengthFt / 2) * Math.cos(theta);
    const localY = (widthFt / 2) * Math.sin(theta);

    // Rotate ellipse to align with flow direction
    const rotatedX = localX * Math.cos(angleRad) - localY * Math.sin(angleRad);
    const rotatedY = localX * Math.sin(angleRad) + localY * Math.cos(angleRad);

    // Convert to lat/lon - ellipse is centered at plumeCenterLat/Lon
    let lat = plumeCenterLat + feetToLatDegrees(rotatedY);
    let lon = plumeCenterLon + feetToLonDegrees(rotatedX, plumeCenterLat);

    // STEP 3: Apply SINK effect from recovery wells - EXACT SAME AS 2D
    for (const well of wells) {
      if (!well.latitude || !well.longitude) continue;

      // Vector FROM plume center TO this boundary point
      const pointFromCenterLatFt = (lat - plumeCenterLat) * 364000;
      const pointFromCenterLonFt = (lon - plumeCenterLon) * 364000 * Math.cos(lat * Math.PI / 180);

      // Vector FROM plume center TO well
      const wellFromCenterLatFt = (well.latitude - plumeCenterLat) * 364000;
      const wellFromCenterLonFt = (well.longitude - plumeCenterLon) * 364000 * Math.cos(plumeCenterLat * Math.PI / 180);

      // Dot product: positive means same general direction
      const dotProduct = pointFromCenterLatFt * wellFromCenterLatFt + pointFromCenterLonFt * wellFromCenterLonFt;

      // Only affect points on same side of plume as well
      if (dotProduct < 0) continue;

      // Vector FROM boundary point TO well (direction of pull)
      const toWellLatFt = (well.latitude - lat) * 364000;
      const toWellLonFt = (well.longitude - lon) * 364000 * Math.cos(lat * Math.PI / 180);
      const distToWellFt = Math.sqrt(toWellLatFt * toWellLatFt + toWellLonFt * toWellLonFt);

      if (distToWellFt < 5) continue;

      // =================================================================
      // DYNAMIC SINK PHYSICS - EXACT SAME AS 2D
      // =================================================================

      const baseSinkRadiusFt = Math.sqrt(well.pump_rate_gpd) * 1.75;
      const captureEfficiency = 0.4 + (concentrationLevel * 0.6);
      const effectiveSinkRadius = baseSinkRadiusFt * captureEfficiency;

      if (distToWellFt > effectiveSinkRadius) continue;

      const normalizedDist = distToWellFt / effectiveSinkRadius;
      const pullStrength = Math.cos(normalizedDist * Math.PI / 2);

      const baseMaxPullFt = Math.sqrt(well.pump_rate_gpd) * 0.35;
      const effectiveMaxPull = baseMaxPullFt * captureEfficiency;
      const pullFt = pullStrength * effectiveMaxPull;

      // Normalize direction to well
      const dirToWellLat = toWellLatFt / distToWellFt;
      const dirToWellLon = toWellLonFt / distToWellFt;

      // PULL BOUNDARY POINT TOWARD THE WELL
      lat += dirToWellLat * pullFt / 364000;
      lon += dirToWellLon * pullFt / (364000 * Math.cos(lat * Math.PI / 180));
    }

    // Convert from lat/lon to 3D coordinates
    const eastFt = (lon - sourceLon) * 364000 * Math.cos(sourceLat * Math.PI / 180);
    const northFt = (lat - sourceLat) * 364000;

    // Map geographic to 3D: East -> +X, North -> -Z (standard 3D with north up when viewed from above)
    const x = eastFt * SCALE;
    const z = -northFt * SCALE;

    points.push(new THREE.Vector3(x, 0, z));
  }

  return points;
}

// Convert lat/lon polygon to 3D scene coordinates
function polygonTo3DPoints(
  polygon: [number, number][],
  sourceLatLon: { lat: number; lon: number },
): THREE.Vector3[] {
  const sourceLat = sourceLatLon.lat;
  const sourceLon = sourceLatLon.lon;

  return polygon.map(([lat, lon]) => {
    // Convert from lat/lon to feet offset from source
    const eastFt = (lon - sourceLon) * 364000 * Math.cos(sourceLat * Math.PI / 180);
    const northFt = (lat - sourceLat) * 364000;

    // Map to 3D: East -> +X, North -> -Z
    const x = eastFt * SCALE;
    const z = -northFt * SCALE;

    return new THREE.Vector3(x, 0, z);
  });
}

// Dissolved Plume with concentration gradient layers and recovery well capture zones
// Uses WORLD coordinates: X = East, Z = South (matches 2D map orientation)
// Can render either:
// 1. Actual polygon coordinates from AGMR extraction (when polygon prop is provided)
// 2. Generated ellipse from length/width (default behavior)
function DissolvedPlumeEllipsoid({
  lengthFt,
  widthFt,
  depthToWaterFt,
  flowDirection,
  sourceLatLon,
  maxConcUgL,
  peakConcUgL,
  recoveryWells = [],
  polygon,
}: {
  lengthFt: number;
  widthFt: number;
  depthToWaterFt: number;
  flowDirection: number;
  sourceLatLon: { lat: number; lon: number };
  maxConcUgL?: number;
  peakConcUgL?: number;
  recoveryWells?: RecoveryWell[];
  // Optional actual polygon coordinates from AGMR extraction
  polygon?: [number, number][] | null;
}) {
  // Debug logging removed for performance

  // Generate distorted boundary shapes for each concentration layer
  const boundaryShapes = useMemo(() => {
    // If we have actual polygon coordinates from extraction, use them
    if (polygon && polygon.length >= 3) {
      // Convert polygon to 3D points
      const outerPoints = polygonTo3DPoints(polygon, sourceLatLon);

      // Calculate centroid for scaling inner layers
      const centroidX = outerPoints.reduce((sum, p) => sum + p.x, 0) / outerPoints.length;
      const centroidZ = outerPoints.reduce((sum, p) => sum + p.z, 0) / outerPoints.length;

      // Generate concentration zones by scaling the polygon toward its centroid
      const layers = [
        { scale: 1.0, concLevel: 0.1, color: COLORS.dissolved.outer, opacity: 0.2, name: 'boundary' },
        { scale: 0.85, concLevel: 0.25, color: COLORS.dissolved.outer, opacity: 0.35, name: 'outer' },
        { scale: 0.70, concLevel: 0.5, color: COLORS.dissolved.core, opacity: 0.5, name: 'mid' },
        { scale: 0.55, concLevel: 0.75, color: '#0891b2', opacity: 0.6, name: 'high' },
        { scale: 0.40, concLevel: 0.9, color: '#0369a1', opacity: 0.7, name: 'core' },
      ];

      return layers.map(layer => {
        // Scale polygon toward centroid for inner concentration zones
        const scaledPoints = outerPoints.map(p => new THREE.Vector3(
          centroidX + (p.x - centroidX) * layer.scale,
          0,
          centroidZ + (p.z - centroidZ) * layer.scale,
        ));

        // Pre-compute THREE.Shape to avoid creating geometry objects during render
        const shape = new THREE.Shape();
        if (scaledPoints.length > 0) {
          shape.moveTo(scaledPoints[0].x, scaledPoints[0].z);
          for (let i = 1; i < scaledPoints.length; i++) {
            shape.lineTo(scaledPoints[i].x, scaledPoints[i].z);
          }
          shape.closePath();
        }

        return {
          ...layer,
          points: scaledPoints,
          shape,
        };
      });
    }

    // Fallback: generate ellipse from length/width
    if (lengthFt <= 0 || widthFt <= 0) return null;

    const migrationOffset = lengthFt * 0.5; // Match 2D map's migration calculation

    // Generate boundaries for each concentration level
    // MATCH 2D exactly: uses scaleFactor = 1 - (idx * 0.15)
    // 2D concentration levels: [0.9, 0.75, 0.5, 0.25, 0.1] reversed gives [0.1, 0.25, 0.5, 0.75, 0.9]
    // Scale factors (outer to inner): 1.0, 0.85, 0.70, 0.55, 0.40
    const layers = [
      { scale: 1.0, concLevel: 0.1, color: COLORS.dissolved.outer, opacity: 0.2, name: 'boundary' },
      { scale: 0.85, concLevel: 0.25, color: COLORS.dissolved.outer, opacity: 0.35, name: 'outer' },
      { scale: 0.70, concLevel: 0.5, color: COLORS.dissolved.core, opacity: 0.5, name: 'mid' },
      { scale: 0.55, concLevel: 0.75, color: '#0891b2', opacity: 0.6, name: 'high' },
      { scale: 0.40, concLevel: 0.9, color: '#0369a1', opacity: 0.7, name: 'core' },
    ];

    return layers.map(layer => {
      const points = generateDistortedEllipseBoundary(
        lengthFt * layer.scale,      // Scale length to match 2D
        widthFt * layer.scale,       // Scale width to match 2D
        migrationOffset * layer.scale, // Scale migration to match 2D
        flowDirection,
        recoveryWells || [],
        sourceLatLon,
        layer.concLevel,
        64
      );

      // Pre-compute THREE.Shape to avoid creating geometry objects during render
      const shape = new THREE.Shape();
      if (points.length > 0) {
        shape.moveTo(points[0].x, points[0].z);
        for (let i = 1; i < points.length; i++) {
          shape.lineTo(points[i].x, points[i].z);
        }
        shape.closePath();
      }

      return {
        ...layer,
        points,
        shape,
      };
    });
  }, [lengthFt, widthFt, flowDirection, sourceLatLon, recoveryWells, polygon]);

  // Plume vertical extent - flat disk in aquifer
  const plumeVerticalExtent = Math.min(widthFt * 0.1, 10);

  // Pre-compute geometries to avoid recreating every render
  const layerGeometries = useMemo(() => {
    if (!boundaryShapes) return null;
    return boundaryShapes.map((layer, idx) => {
      const shape = new THREE.Shape();
      const smoothedPoints = smoothVector3Boundary(layer.points, 1, 0.5);
      if (smoothedPoints.length > 0) {
        shape.moveTo(smoothedPoints[0].x, smoothedPoints[0].z);
        for (let i = 1; i < smoothedPoints.length; i++) {
          shape.lineTo(smoothedPoints[i].x, smoothedPoints[i].z);
        }
        shape.closePath();
      }
      const extrudeSettings = {
        steps: 1,
        depth: plumeVerticalExtent * SCALE * (1 - idx * 0.1),
        bevelEnabled: false,
      };
      return { ...layer, geometry: new THREE.ExtrudeGeometry(shape, extrudeSettings) };
    });
  }, [boundaryShapes, plumeVerticalExtent]);

  if (!layerGeometries) return null;

  const yPos = -(depthToWaterFt + plumeVerticalExtent / 2 + 2) * SCALE;
  const concFactor = peakConcUgL && maxConcUgL ? Math.min(1, maxConcUgL / peakConcUgL) : 1;
  const baseOpacity = 0.3 + concFactor * 0.5;

  return (
    <group position={[0, yPos, 0]}>
      {layerGeometries.map((layer, idx) => (
        <mesh
          key={`plume-layer-${layer.name}`}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, idx * 0.02, 0]}
          geometry={layer.geometry}
        >
          <meshStandardMaterial
            color={layer.color}
            transparent
            opacity={layer.opacity * baseOpacity}
            emissive={layer.color}
            emissiveIntensity={0.3}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

// Multi-Contaminant Plume 3D - Renders a single compound's plume from well measurements
// Uses the same rendering approach as DissolvedPlumeEllipsoid but with custom colors
const MultiContaminantPlume3D = React.memo(function MultiContaminantPlume3D({
  polygon,
  depthToWaterFt,
  sourceLatLon,
  coreColor,
  outerColor,
  compound,
  verticalOffset = 0,
}: {
  polygon: [number, number][];
  depthToWaterFt: number;
  sourceLatLon: { lat: number; lon: number };
  coreColor: string;
  outerColor: string;
  compound: string;
  verticalOffset?: number;
}) {
  // Convert polygon to 3D points
  const boundaryShapes = useMemo(() => {
    if (!polygon || polygon.length < 3) return null;

    const outerPoints = polygonTo3DPoints(polygon, sourceLatLon);

    // Calculate centroid for scaling inner layers
    const centroidX = outerPoints.reduce((sum, p) => sum + p.x, 0) / outerPoints.length;
    const centroidZ = outerPoints.reduce((sum, p) => sum + p.z, 0) / outerPoints.length;

    // Generate concentration zones by scaling toward centroid
    const layers = [
      { scale: 1.0, opacity: 0.15, color: outerColor, name: 'boundary' },
      { scale: 0.7, opacity: 0.3, color: outerColor, name: 'mid' },
      { scale: 0.4, opacity: 0.5, color: coreColor, name: 'core' },
    ];

    return layers.map(layer => {
      const scaledPoints = outerPoints.map(p => new THREE.Vector3(
        centroidX + (p.x - centroidX) * layer.scale,
        0,
        centroidZ + (p.z - centroidZ) * layer.scale,
      ));

      // Pre-compute THREE.Shape to avoid creating geometry objects during render
      const shape = new THREE.Shape();
      if (scaledPoints.length > 0) {
        shape.moveTo(scaledPoints[0].x, scaledPoints[0].z);
        for (let i = 1; i < scaledPoints.length; i++) {
          shape.lineTo(scaledPoints[i].x, scaledPoints[i].z);
        }
        shape.closePath();
      }

      return { ...layer, points: scaledPoints, shape };
    });
  }, [polygon, sourceLatLon, coreColor, outerColor]);

  // Pre-compute geometries in useMemo to avoid recreating every render
  const plumeThickness = 8;  // ft
  const layerGeometries = useMemo(() => {
    if (!boundaryShapes) return null;
    return boundaryShapes.map((layer, idx) => {
      const shape = new THREE.Shape();
      const smoothedPoints = smoothVector3Boundary(layer.points, 1, 0.5);
      if (smoothedPoints.length > 0) {
        shape.moveTo(smoothedPoints[0].x, smoothedPoints[0].z);
        for (let i = 1; i < smoothedPoints.length; i++) {
          shape.lineTo(smoothedPoints[i].x, smoothedPoints[i].z);
        }
        shape.closePath();
      }
      const extrudeSettings = {
        steps: 1,
        depth: plumeThickness * SCALE * (1 - idx * 0.2),
        bevelEnabled: false,
      };
      return { ...layer, geometry: new THREE.ExtrudeGeometry(shape, extrudeSettings) };
    });
  }, [boundaryShapes]);

  if (!layerGeometries) return null;

  const yPos = -(depthToWaterFt + plumeThickness / 2 + 2 + verticalOffset) * SCALE;

  return (
    <group position={[0, yPos, 0]}>
      {layerGeometries.map((layer, idx) => (
        <mesh
          key={`${compound}-layer-${layer.name}`}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, idx * 0.02, 0]}
          geometry={layer.geometry}
        >
          <meshStandardMaterial
            color={layer.color}
            transparent
            opacity={layer.opacity}
            emissive={layer.color}
            emissiveIntensity={0.2}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
});

// ============================================================================
// VOXEL GRID PLUME RENDERING (MODFLOW-style concentration field)
// ============================================================================

interface Voxel3D {
  x: number;  // Scene X coordinate
  y: number;  // Scene Y coordinate (depth)
  z: number;  // Scene Z coordinate
  color: THREE.Color;
  opacity: number;
}

/**
 * Custom shader material for instanced voxels with per-instance opacity.
 * Creates smooth, continuous concentration gradients like MODFLOW visualization.
 */
function createVoxelMaterial(baseColor: string, emissiveIntensity: number = 0.3) {
  return new THREE.ShaderMaterial({
    uniforms: {
      emissiveColor: { value: new THREE.Color(baseColor) },
      emissiveIntensity: { value: emissiveIntensity },
    },
    vertexShader: `
      attribute vec3 instanceColor;
      attribute float instanceOpacity;
      varying vec3 vColor;
      varying float vOpacity;

      void main() {
        vColor = instanceColor;
        vOpacity = instanceOpacity;
        vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 emissiveColor;
      uniform float emissiveIntensity;
      varying vec3 vColor;
      varying float vOpacity;

      void main() {
        vec3 finalColor = vColor + emissiveColor * emissiveIntensity;
        gl_FragColor = vec4(finalColor, vOpacity);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
  });
}

/**
 * Generate a fine voxel grid for 3D plume rendering (MODFLOW-style).
 * Creates a continuous concentration field visualization with smooth gradients.
 */
function generateVoxelGrid(
  rawBoundary: [number, number][],
  wells: Array<{ lat: number; lon: number; concentration: number }>,
  maxConcentration: number,
  baseColor: string,
  sourceLatLon: { lat: number; lon: number },
  depthToWaterFt: number,
  gridResolution: number = 240,
  cleanWells: Array<{ lat: number; lon: number }> = []
): { voxels: Voxel3D[]; voxelSize: number } {
  if (rawBoundary.length < 3) return { voxels: [], voxelSize: 0 };

  // Smooth the boundary for organic curves (1 iteration is sufficient for 3D distant view)
  // Use same Chaikin smoothing as 2D (4 iterations for raw hulls ≤10 points)
  const boundary = rawBoundary.length <= 10
    ? smoothLatLonBoundary(rawBoundary, 4)
    : rawBoundary;

  const voxels: Voxel3D[] = [];
  const CLEAN_WELL_EXCLUSION_FT = 75;
  const sourceLat = sourceLatLon.lat;
  const sourceLon = sourceLatLon.lon;

  // Calculate bounds from smoothed boundary
  const lats = boundary.map(p => p[0]);
  const lons = boundary.map(p => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  const latStep = (maxLat - minLat) / gridResolution;
  const lonStep = (maxLon - minLon) / gridResolution;

  const ftPerDegLat = 364000;
  const ftPerDegLon = ftPerDegLat * Math.cos(sourceLat * Math.PI / 180);
  const voxelSizeFt = Math.max(latStep * ftPerDegLat, lonStep * ftPerDegLon) * 1.2;
  const voxelSize = voxelSizeFt * SCALE;

  function isInsidePolygon(lat: number, lon: number): boolean {
    let inside = false;
    for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
      const [yi, xi] = boundary[i];
      const [yj, xj] = boundary[j];
      if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }

  function isNearCleanWell(lat: number, lon: number): boolean {
    if (cleanWells.length === 0) return false;
    for (const well of cleanWells) {
      const dLat = (lat - well.lat) * ftPerDegLat;
      const dLon = (lon - well.lon) * ftPerDegLon;
      const distFt = Math.sqrt(dLat * dLat + dLon * dLon);
      if (distFt < CLEAN_WELL_EXCLUSION_FT) return true;
    }
    return false;
  }


  function interpolateConcentration(lat: number, lon: number): number {
    if (wells.length === 0) return maxConcentration * 0.5;
    let sumWeights = 0;
    let sumWeightedConc = 0;
    const power = 2;

    for (const well of wells) {
      const dLat = (lat - well.lat) * ftPerDegLat;
      const dLon = (lon - well.lon) * ftPerDegLon;
      const dist = Math.sqrt(dLat * dLat + dLon * dLon);
      if (dist < 1) return well.concentration;
      const weight = 1 / Math.pow(dist, power);
      sumWeights += weight;
      sumWeightedConc += weight * well.concentration;
    }
    return sumWeights > 0 ? sumWeightedConc / sumWeights : 0;
  }

  const logMax = maxConcentration > 0 ? Math.log10(maxConcentration + 1) : 1;

  function getConcentrationRatio(concentration: number): number {
    if (maxConcentration <= 0) return 0;
    const logConc = Math.log10(concentration + 1);
    return Math.min(1, Math.max(0, logConc / logMax));
  }

  // Pre-compute 256-step color + opacity lookup table (avoids new THREE.Color per voxel)
  const COLOR_STEPS = 256;
  const hex = baseColor.replace('#', '');
  const baseR = parseInt(hex.substring(0, 2), 16) / 255;
  const baseG = parseInt(hex.substring(2, 4), 16) / 255;
  const baseB = parseInt(hex.substring(4, 6), 16) / 255;

  const colorLUT: THREE.Color[] = new Array(COLOR_STEPS);
  const opacityLUT: number[] = new Array(COLOR_STEPS);
  for (let s = 0; s < COLOR_STEPS; s++) {
    const ratio = s / (COLOR_STEPS - 1);
    const darkFactor = 0.15 + (1 - ratio) * 0.75;
    const lightBlend = (1 - ratio) * 0.7;
    colorLUT[s] = new THREE.Color(
      Math.min(1, baseR * darkFactor + lightBlend),
      Math.min(1, baseG * darkFactor + lightBlend),
      Math.min(1, baseB * darkFactor + lightBlend)
    );
    opacityLUT[s] = 0.4 + ratio * 0.55;
  }

  function getConcentrationColor(concentration: number): THREE.Color {
    const ratio = getConcentrationRatio(concentration);
    return colorLUT[Math.round(ratio * (COLOR_STEPS - 1))];
  }

  function getConcentrationOpacity(concentration: number): number {
    const ratio = getConcentrationRatio(concentration);
    return opacityLUT[Math.round(ratio * (COLOR_STEPS - 1))];
  }

  const plumeThicknessFt = 6;
  const baseY = -(depthToWaterFt + plumeThicknessFt / 2 + 2) * SCALE;

  for (let i = 0; i <= gridResolution; i++) {
    for (let j = 0; j <= gridResolution; j++) {
      const lat = minLat + i * latStep;
      const lon = minLon + j * lonStep;

      if (isInsidePolygon(lat, lon)) {
        const conc = interpolateConcentration(lat, lon);
        const ratio = getConcentrationRatio(conc);
        if (ratio < 0.02) continue;

        const eastFt = (lon - sourceLon) * ftPerDegLon;
        const northFt = (lat - sourceLat) * ftPerDegLat;

        voxels.push({
          x: eastFt * SCALE,
          y: baseY,
          z: -northFt * SCALE,
          color: getConcentrationColor(conc),
          opacity: getConcentrationOpacity(conc),
        });
      }
    }
  }

  return { voxels, voxelSize };
}

/**
 * Voxel Grid Plume 3D - MODFLOW-style concentration field visualization
 */
const VoxelGridPlume3D = React.memo(function VoxelGridPlume3D({
  polygon,
  wells,
  cleanWells,
  maxConcentration,
  depthToWaterFt,
  sourceLatLon,
  baseColor = '#06b6d4',
  compound,
  gridResolution = 240,
}: {
  polygon: [number, number][];
  wells: Array<{ lat: number; lon: number; concentration: number }>;
  cleanWells?: Array<{ lat: number; lon: number }>;
  maxConcentration: number;
  depthToWaterFt: number;
  sourceLatLon: { lat: number; lon: number };
  baseColor?: string;
  compound?: string;
  gridResolution?: number;
}) {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  const { voxels, voxelSize } = useMemo(() => {
    if (!polygon || polygon.length < 3) return { voxels: [], voxelSize: 0 };
    return generateVoxelGrid(
      polygon, wells, maxConcentration, baseColor,
      sourceLatLon, depthToWaterFt, gridResolution, cleanWells || []
    );
  }, [polygon, wells, maxConcentration, baseColor, sourceLatLon, depthToWaterFt, gridResolution, cleanWells]);

  const shaderMaterial = useMemo(() => createVoxelMaterial(baseColor, 0.25), [baseColor]);

  useEffect(() => {
    if (!meshRef.current || voxels.length === 0) return;

    const mesh = meshRef.current;
    const geometry = mesh.geometry;
    const tempObject = new THREE.Object3D();
    const colorArray = new Float32Array(voxels.length * 3);
    const opacityArray = new Float32Array(voxels.length);

    voxels.forEach((voxel, i) => {
      tempObject.position.set(voxel.x, voxel.y, voxel.z);
      tempObject.scale.setScalar(voxelSize);
      tempObject.updateMatrix();
      mesh.setMatrixAt(i, tempObject.matrix);

      colorArray[i * 3] = voxel.color.r;
      colorArray[i * 3 + 1] = voxel.color.g;
      colorArray[i * 3 + 2] = voxel.color.b;
      opacityArray[i] = voxel.opacity;
    });

    geometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(colorArray, 3));
    geometry.setAttribute('instanceOpacity', new THREE.InstancedBufferAttribute(opacityArray, 1));
    mesh.instanceMatrix.needsUpdate = true;
  }, [voxels, voxelSize]);

  if (voxels.length === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, voxels.length]}
      frustumCulled={false}
      material={shaderMaterial}
    >
      <boxGeometry args={[1, 0.3, 1]} />
    </instancedMesh>
  );
});

// Alias for backwards compatibility
const ParticleBasedPlume3D = VoxelGridPlume3D;

/**
 * AnimatedStackGroup — smoothly animates Y position via useFrame (no React state churn).
 * Replaces the old approach where stackSeparation was in React state, causing
 * compoundPositions useMemo + all VoxelGridPlume3D components to re-render 60x/sec.
 */
function AnimatedStackGroup({
  targetY,
  children,
  ...props
}: {
  targetY: number;
  children: React.ReactNode;
} & Omit<JSX.IntrinsicElements['group'], 'children'>) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame(() => {
    if (!groupRef.current) return;
    const diff = targetY - groupRef.current.position.y;
    if (Math.abs(diff) < 0.01) {
      groupRef.current.position.y = targetY;
    } else {
      groupRef.current.position.y += diff * 0.12;
    }
  });
  return (
    <group ref={groupRef} {...props}>
      {children}
    </group>
  );
}

// Vapor Plume - Hemisphere above PSH in vadose zone with concentration gradient
function VaporPlume({
  areaSqft,
  depthToWaterFt,
  vaporDepthFt,
  maxConcUgM3,
  peakConcUgM3,
}: {
  areaSqft: number;
  depthToWaterFt: number;
  vaporDepthFt: number;
  maxConcUgM3?: number;
  peakConcUgM3?: number;
}) {
  const radiusFt = areaToRadius(areaSqft);

  if (areaSqft <= 0) return null;

  // Vapor rises from water table into vadose zone
  // Center it between water table and surface
  const vaporHeight = Math.min(vaporDepthFt, depthToWaterFt);
  const yPos = -(depthToWaterFt - vaporHeight / 2) * SCALE;

  // Calculate concentration factor
  const concFactor = peakConcUgM3 && maxConcUgM3 ? Math.min(1, maxConcUgM3 / peakConcUgM3) : 1;
  const baseOpacity = 0.2 + concFactor * 0.4; // 0.2 to 0.6 (vapor is more transparent)

  return (
    <group position={[0, yPos, 0]}>
      {/* High concentration core near source */}
      <mesh>
        <sphereGeometry args={[radiusFt * SCALE * 0.4, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#a21caf"
          transparent
          opacity={baseOpacity}
          emissive="#a21caf"
          emissiveIntensity={0.5}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Medium concentration zone */}
      <mesh>
        <sphereGeometry args={[radiusFt * SCALE * 0.7, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={COLORS.vapor.core}
          transparent
          opacity={baseOpacity * 0.6}
          emissive={COLORS.vapor.core}
          emissiveIntensity={0.35}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Low concentration outer zone */}
      <mesh>
        <sphereGeometry args={[radiusFt * SCALE, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={COLORS.vapor.outer}
          transparent
          opacity={baseOpacity * 0.35}
          emissive={COLORS.vapor.outer}
          emissiveIntensity={0.2}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Outer boundary/detection limit */}
      <mesh>
        <sphereGeometry args={[radiusFt * SCALE * 1.15, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={COLORS.vapor.outer}
          transparent
          opacity={0.1}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

// Recovery Well - Vertical cylinder with screen
// Position uses lat/lon converted to world coords: X = East, Z = South
const RecoveryWellMesh = React.memo(function RecoveryWellMesh({
  well,
  depthToWaterFt,
  aquiferThicknessFt,
  sourceLatLon,
}: {
  well: RecoveryWell;
  depthToWaterFt: number;
  aquiferThicknessFt: number;
  sourceLatLon: { lat: number; lon: number };
}) {
  // Convert well lat/lon to world coordinates (feet from source)
  // X = East (positive = east), Z = South (positive = south)
  const eastFt = well.latitude && well.longitude
    ? (well.longitude - sourceLatLon.lon) * 364000 * Math.cos(sourceLatLon.lat * Math.PI / 180)
    : 0;
  const southFt = well.latitude && well.longitude
    ? -(well.latitude - sourceLatLon.lat) * 364000  // Negative because lower lat = south
    : 0;

  const xPos = eastFt * SCALE;
  const zPos = southFt * SCALE;

  // Use actual per-well depth if available, otherwise estimate
  const wellLength = well.depth_ft || (depthToWaterFt + aquiferThicknessFt * 0.7);
  const yPos = -wellLength * SCALE / 2;

  return (
    <group position={[xPos, 0, zPos]}>
      {/* Well casing */}
      <mesh position={[0, yPos, 0]}>
        <cylinderGeometry args={[0.15, 0.15, wellLength * SCALE, 8]} />
        <meshStandardMaterial
          color={COLORS.recoveryWell}
          emissive={COLORS.recoveryWell}
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Pump housing at surface */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[0.4, 0.6, 0.4]} />
        <meshStandardMaterial
          color={COLORS.recoveryWell}
          emissive={COLORS.recoveryWell}
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Label */}
      <Text
        position={[0, 1, 0]}
        fontSize={0.4}
        color="white"
        anchorX="center"
        anchorY="bottom"
      >
        {`R: ${(well.pump_rate_gpd / 1000).toFixed(0)}k GPD`}
      </Text>
    </group>
  );
});

// MDPE Well - Similar but green
// Position uses lat/lon converted to world coords: X = East, Z = South
const MDPEWellMesh = React.memo(function MDPEWellMesh({
  well,
  depthToWaterFt,
  sourceLatLon,
}: {
  well: MDPEWell;
  depthToWaterFt: number;
  sourceLatLon: { lat: number; lon: number };
}) {
  // Convert well lat/lon to world coordinates (feet from source)
  const eastFt = well.latitude && well.longitude
    ? (well.longitude - sourceLatLon.lon) * 364000 * Math.cos(sourceLatLon.lat * Math.PI / 180)
    : 0;
  const southFt = well.latitude && well.longitude
    ? -(well.latitude - sourceLatLon.lat) * 364000
    : 0;

  const xPos = eastFt * SCALE;
  const zPos = southFt * SCALE;

  // Use actual per-well depth if available, otherwise estimate (vadose zone depth)
  const wellLength = well.depth_ft || (depthToWaterFt * 0.8);
  const yPos = -wellLength * SCALE / 2;

  return (
    <group position={[xPos, 0, zPos]}>
      {/* Well casing */}
      <mesh position={[0, yPos, 0]}>
        <cylinderGeometry args={[0.15, 0.15, wellLength * SCALE, 8]} />
        <meshStandardMaterial
          color={COLORS.mdpeWell}
          emissive={COLORS.mdpeWell}
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Extraction unit at surface */}
      <mesh position={[0, 0.4, 0]}>
        <cylinderGeometry args={[0.3, 0.3, 0.8, 8]} />
        <meshStandardMaterial
          color={COLORS.mdpeWell}
          emissive={COLORS.mdpeWell}
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Label */}
      <Text
        position={[0, 1.2, 0]}
        fontSize={0.4}
        color="white"
        anchorX="center"
        anchorY="bottom"
      >
        {`M: ${well.extraction_cfm} CFM`}
      </Text>
    </group>
  );
});

// Monitoring Well - Cyan color for display
// Position uses lat/lon converted to world coords: X = East, Z = South
const MonitoringWellMesh = React.memo(function MonitoringWellMesh({
  well,
  depthToWaterFt,
  aquiferThicknessFt,
  sourceLatLon,
}: {
  well: MonitoringWellMarker;
  depthToWaterFt: number;
  aquiferThicknessFt: number;
  sourceLatLon: { lat: number; lon: number };
}) {
  // Convert well lat/lon to world coordinates (feet from source)
  const eastFt = well.latitude && well.longitude
    ? (well.longitude - sourceLatLon.lon) * 364000 * Math.cos(sourceLatLon.lat * Math.PI / 180)
    : 0;
  const southFt = well.latitude && well.longitude
    ? -(well.latitude - sourceLatLon.lat) * 364000
    : 0;

  const xPos = eastFt * SCALE;
  const zPos = southFt * SCALE;

  // Well extends from surface into aquifer (use provided depth or default)
  const wellDepth = well.depth_ft || (depthToWaterFt + aquiferThicknessFt * 0.5);
  const yPos = -wellDepth * SCALE / 2;

  return (
    <group position={[xPos, 0, zPos]}>
      {/* Well casing - Cyan color */}
      <mesh position={[0, yPos, 0]}>
        <cylinderGeometry args={[0.12, 0.12, wellDepth * SCALE, 8]} />
        <meshStandardMaterial
          color="#22d3ee"
          emissive="#22d3ee"
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Well head cap at surface */}
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 0.3, 8]} />
        <meshStandardMaterial
          color="#06b6d4"
          emissive="#06b6d4"
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Label */}
      <Text
        position={[0, 0.8, 0]}
        fontSize={0.3}
        color="#22d3ee"
        anchorX="center"
        anchorY="bottom"
      >
        {well.well_id}
      </Text>
    </group>
  );
});

// Source marker
const SourceMarker = React.memo(function SourceMarker() {
  return (
    <group position={[0, 0.3, 0]}>
      <mesh>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshStandardMaterial
          color={COLORS.source}
          emissive={COLORS.source}
          emissiveIntensity={0.5}
        />
      </mesh>
      <Text
        position={[0, 0.8, 0]}
        fontSize={0.4}
        color="white"
        anchorX="center"
        anchorY="bottom"
      >
        SOURCE
      </Text>
    </group>
  );
});

// Axis labels and scale
const AxisLabels = React.memo(function AxisLabels({ depthToWaterFt }: { depthToWaterFt: number }) {
  return (
    <group>
      {/* Depth markers */}
      <Text
        position={[-12, 0.5, 0]}
        fontSize={0.35}
        color="#888"
        anchorX="right"
      >
        Surface (0 ft)
      </Text>
      <Text
        position={[-12, -depthToWaterFt * SCALE, 0]}
        fontSize={0.35}
        color="#4a90d9"
        anchorX="right"
      >
        {`Water Table (${Math.round(depthToWaterFt * 10) / 10} ft)`}
      </Text>
    </group>
  );
});

// Compass rose with N/E/S/W labels to match 2D map orientation
// In 2D map: North is UP (negative Y in screen), South is DOWN
// In 3D (looking down from above, matching 2D map view):
//   -Z = North (top of screen when looking down)
//   +Z = South (bottom of screen)
//   +X = East (right)
//   -X = West (left)
const CompassRose = React.memo(function CompassRose({ flowDirection }: { flowDirection: number }) {
  const compassRadius = 10;

  // Flow direction arrow
  // Convert compass bearing to 3D coordinates
  // 0° (N) = -Z, 90° (E) = +X, 180° (S) = +Z, 270° (W) = -X
  const flowRad = (flowDirection - 90) * Math.PI / 180;
  const flowEndX = 6 * Math.cos(flowRad);
  const flowEndZ = 6 * Math.sin(flowRad);

  return (
    <group position={[0, 0.1, 0]}>
      {/* Compass directions - fixed positions matching 2D map orientation */}
      {/* North = -Z (top when looking down) */}
      <Text
        position={[0, 0.3, -compassRadius]}
        fontSize={0.6}
        color="#ef4444"
        anchorX="center"
        fontWeight="bold"
      >
        N
      </Text>

      {/* South = +Z (bottom when looking down) */}
      <Text
        position={[0, 0.3, compassRadius]}
        fontSize={0.5}
        color="#94a3b8"
        anchorX="center"
      >
        S
      </Text>

      {/* East = +X (right when looking down) */}
      <Text
        position={[compassRadius, 0.3, 0]}
        fontSize={0.5}
        color="#94a3b8"
        anchorX="center"
      >
        E
      </Text>

      {/* West = -X (left when looking down) */}
      <Text
        position={[-compassRadius, 0.3, 0]}
        fontSize={0.5}
        color="#94a3b8"
        anchorX="center"
      >
        W
      </Text>

      {/* Flow direction arrow from source */}
      <Line
        points={[[0, 0, 0], [flowEndX, 0, flowEndZ]]}
        color="#60a5fa"
        lineWidth={3}
      />
      {/* Arrowhead */}
      <mesh position={[flowEndX, 0, flowEndZ]} rotation={[-Math.PI / 2, 0, -flowRad]}>
        <coneGeometry args={[0.25, 0.6, 8]} />
        <meshStandardMaterial color="#60a5fa" />
      </mesh>
      <Text
        position={[flowEndX * 1.3, 0.5, flowEndZ * 1.3]}
        fontSize={0.3}
        color="#60a5fa"
        anchorX="center"
      >
        {`Flow ${flowDirection}°`}
      </Text>
    </group>
  );
});

// Scene props extended for multi-stage inspection
interface SceneProps extends ThreePlume3DViewProps {
  inspectionState: InspectionState;
  onPlumeDoubleClick: (plumeType: FocusedPlumeType) => void;
  onDissolvedDoubleClick: () => void;  // Advances to pancake stack
  onCompoundDoubleClick: (compound: string) => void;  // Selects single compound
  orbitControlsRef: React.RefObject<any>;
  gradientMap?: SiteGradientMap | null;
  showGradientContours?: boolean;
  multiContaminantBoundaries?: ContaminantBoundaryData[];
  visibleCompounds?: Set<string>;
  // PSH well data from database (actual gauging measurements)
  pshWells?: Array<{ well_id: string; lat: number; lon: number; product_thickness_ft: number; depth_to_water_ft?: number }>;
  onPshMetadata?: (meta: PSHMetadata) => void;
  pshVE?: number;  // PSH vertical exaggeration (1–35)
}

// Main 3D Scene
function Scene({
  plumeState,
  depthToWaterFt,
  aquiferThicknessFt,
  flowDirection,
  recoveryWells = [],
  mdpeWells = [],
  monitoringWells = [],
  sourceLatLon,
  visibleLayers = { psh: true, dissolved: true, vapor: false },
  initialPshMassLbs,
  peakDissolvedConcUgL,
  peakVaporConcUgM3,
  inspectionState,
  onPlumeDoubleClick,
  onDissolvedDoubleClick,
  onCompoundDoubleClick,
  orbitControlsRef,
  pshWells = [],
  onPshMetadata,
  pshVE = 1,
  gradientMap,
  showGradientContours = true,
  multiContaminantBoundaries = [],
  visibleCompounds,
}: SceneProps) {
  // Default sourceLatLon if not provided — stable reference to prevent useMemo churn
  const srcLatLon = useMemo(
    () => sourceLatLon || { lat: 0, lon: 0 },
    [sourceLatLon]
  );
  const sceneSize = 25; // Scene extends 25 units (250 ft) in each direction

  // Derive effective depth-to-water from actual PSH well gauging data.
  // Per-well depth_to_water_ft from gauging is the most accurate measurement.
  // Falls back to the site-level prop if no gauging data available.
  const effectiveDepthToWater = useMemo(() => {
    const wellsWithDTW = pshWells.filter(w => w.depth_to_water_ft != null && w.depth_to_water_ft > 0);
    if (wellsWithDTW.length === 0) return depthToWaterFt;
    const meanDTW = wellsWithDTW.reduce((sum, w) => sum + w.depth_to_water_ft!, 0) / wellsWithDTW.length;
    return Math.round(meanDTW * 100) / 100; // 2 decimal places
  }, [pshWells, depthToWaterFt]);

  // Extract inspection state
  const { stage, focusedPlume, selectedCompound } = inspectionState;

  // Target stack separation based on stage (used for camera + AnimatedStackGroup targets)
  const stackSeparationTarget = (stage === 'pancake-stack' || stage === 'single-plume') ? 3.0 : 0;

  // Calculate plume positions for camera targeting (using actual gauging-derived depth)
  const pshYPos = -effectiveDepthToWater * SCALE + ((plumeState.psh?.thickness_ft ?? 0) * SCALE) / 2;
  const dissolvedYPos = -(effectiveDepthToWater + 2) * SCALE;
  const vaporYPos = -(effectiveDepthToWater / 2) * SCALE;

  const plumePositions = useMemo(() => ({
    psh: new THREE.Vector3(0, pshYPos, 0),
    dissolved: new THREE.Vector3(0, dissolvedYPos, 0),
    vapor: new THREE.Vector3(0, vaporYPos, 0),
  }), [pshYPos, dissolvedYPos, vaporYPos]);

  // Calculate compound positions for pancake stack mode (camera targeting only)
  // Uses target value (not animated) — camera has its own smooth lerp
  const compoundPositions = useMemo(() => {
    const positions = new Map<string, THREE.Vector3>();
    const compounds = multiContaminantBoundaries
      .filter(b => !visibleCompounds || visibleCompounds.has(b.compound.toLowerCase()))
      .map(b => b.compound.toLowerCase());

    compounds.forEach((compound, idx) => {
      // Stack vertically with separation
      const yOffset = stackSeparationTarget * (idx - (compounds.length - 1) / 2);
      positions.set(compound, new THREE.Vector3(0, dissolvedYPos + yOffset, 0));
    });

    return positions;
  }, [multiContaminantBoundaries, visibleCompounds, dissolvedYPos, stackSeparationTarget]);

  const defaultCameraPosition = useMemo(() => new THREE.Vector3(15, 10, 15), []);
  const defaultCameraTarget = useMemo(() => new THREE.Vector3(0, -effectiveDepthToWater * SCALE / 2, 0), [effectiveDepthToWater]);

  // Debug logging removed for performance

  // Determine what should be visible based on inspection stage
  const isInspectingDissolved = stage === 'dissolved-focus' || stage === 'pancake-stack' || stage === 'single-plume';
  const showEnvironment = stage === 'overview';
  const showPsh = visibleLayers.psh && (stage === 'overview' || focusedPlume === 'psh');
  const showDissolved = visibleLayers.dissolved && (stage === 'overview' || isInspectingDissolved);
  const showVapor = visibleLayers.vapor && (stage === 'overview' || focusedPlume === 'vapor');
  const showWells = stage === 'overview';

  return (
    <>
      {/* Camera Controller for smooth multi-stage transitions */}
      <CameraController
        inspectionState={inspectionState}
        plumePositions={plumePositions}
        compoundPositions={compoundPositions}
        defaultPosition={defaultCameraPosition}
        defaultTarget={defaultCameraTarget}
        orbitControlsRef={orbitControlsRef}
      />

      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 20, 10]} intensity={0.8} />
      <directionalLight position={[-10, 10, -10]} intensity={0.3} />

      {/* Geological layers - hidden in focus mode */}
      {showEnvironment && (
        <>
          <GroundSurface size={sceneSize} />
          {/* Use gradient contours on water table if available */}
          {gradientMap && gradientMap.contours && gradientMap.contours.length > 0 ? (
            <WaterTableWithContours
              size={sceneSize}
              depth={effectiveDepthToWater}
              contours={gradientMap.contours}
              sourceLatLon={srcLatLon}
              showContours={showGradientContours}
              flowArrows={gradientMap.flow_arrows}
              gradientWells={gradientMap.well_readings}
            />
          ) : (
            <WaterTable size={sceneSize} depth={effectiveDepthToWater} />
          )}
          <SourceMarker />
        </>
      )}

      <group>
        {/* PSH Plume - Use voxel cube with real well data, or fallback to circle */}
        {showPsh && plumeState.psh && (
          <group onDoubleClick={() => onPlumeDoubleClick(focusedPlume === 'psh' ? null : 'psh')}>
            {pshWells.length > 0 ? (
              // Use actual well gauging data for accurate 3D voxel representation
              <PSHVoxelCube
                wells={pshWells}
                sourceLatLon={srcLatLon}
                depthToWaterFt={effectiveDepthToWater}
                gridResolutionXZ={240}
                gridResolutionY={10}
                visible={true}
                onMetadata={onPshMetadata}
                verticalExaggeration={pshVE}
              />
            ) : (
              // Fallback to physics-based circle when no well data available
              <PSHPlume
                areaSqft={plumeState.psh.area_sqft}
                thicknessFt={plumeState.psh.thickness_ft}
                depthToWaterFt={effectiveDepthToWater}
                massLbs={plumeState.psh.mass_lbs}
                initialMassLbs={initialPshMassLbs}
                mdpeWells={mdpeWells}
                sourceLatLon={srcLatLon}
              />
            )}
          </group>
        )}

        {/* Dissolved Plume + Multi-Contaminant Plumes - Multi-Stage Inspection */}
        {showDissolved && (
          <group
            onDoubleClick={(e) => {
              e.stopPropagation();
              // Stage-based click behavior
              if (stage === 'overview') {
                // From overview: zoom to dissolved group
                onPlumeDoubleClick('dissolved');
              } else if (stage === 'dissolved-focus') {
                // From dissolved focus: expand to pancake stack
                onDissolvedDoubleClick();
              }
              // In pancake-stack or single-plume, individual compound handlers take over
            }}
          >

            {/* Invisible click target sphere - captures double-clicks for stage transitions */}
            {(stage === 'overview' || stage === 'dissolved-focus') && (
              <mesh
                position={[0, -(effectiveDepthToWater + 5) * SCALE, 0]}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (stage === 'overview') {
                    onPlumeDoubleClick('dissolved');
                  } else if (stage === 'dissolved-focus') {
                    onDissolvedDoubleClick();
                  }
                }}
              >
                <sphereGeometry args={[8, 8, 6]} />
                <meshBasicMaterial
                  transparent
                  opacity={0.001}
                  depthWrite={false}
                />
              </mesh>
            )}

            {/* Main Dissolved Plume Ellipsoid - only show if NO multi-contaminant data */}
            {/* When we have BTEX particle plumes, they replace this ellipsoid */}
            {(stage === 'overview' || stage === 'dissolved-focus') &&
             (!multiContaminantBoundaries || multiContaminantBoundaries.length === 0) && (
              <DissolvedPlumeEllipsoid
                lengthFt={plumeState.dissolved.length_ft}
                widthFt={plumeState.dissolved.width_ft}
                depthToWaterFt={effectiveDepthToWater}
                flowDirection={flowDirection}
                sourceLatLon={srcLatLon}
                maxConcUgL={plumeState.dissolved.max_conc_ug_l}
                peakConcUgL={peakDissolvedConcUgL}
                recoveryWells={recoveryWells}
                polygon={plumeState.dissolved.polygon}
              />
            )}

            {/* Multi-Contaminant Plumes (BTEX) - PARTICLE-BASED rendering */}
            {multiContaminantBoundaries && multiContaminantBoundaries.length > 0 && (
              <>
                {multiContaminantBoundaries
                  .filter(boundary => {
                    // Filter by visible compounds
                    if (!visibleCompounds) return true;
                    if (!visibleCompounds.has(boundary.compound.toLowerCase())) return false;
                    // In single-plume mode, only show selected compound
                    if (stage === 'single-plume' && selectedCompound !== boundary.compound.toLowerCase()) {
                      return false;
                    }
                    return true;
                  })
                  .map((boundary, idx, filteredArray) => {
                    // Skip if no valid boundary polygon
                    if (!boundary.boundary || boundary.boundary.length < 3) return null;

                    const compoundKey = boundary.compound.toLowerCase().trim();

                    // Get compound-specific color from COMPOUND_PARAMETERS (single source of truth)
                    const baseColor = getCompoundColorFromParams(boundary.compound);

                    // Calculate target vertical offset for pancake stack mode
                    // AnimatedStackGroup lerps to this target via useFrame (no React state churn)
                    const stackOffsetTarget = (stage === 'pancake-stack' || stage === 'single-plume')
                      ? stackSeparationTarget * (idx - (filteredArray.length - 1) / 2)
                      : 0;  // All plumes flush at same elevation

                    // Use ALL wells (above + below threshold) for IDW — matches 2D approach
                    // Exceeding wells pull gradient high, non-exceeding pull it low
                    const aboveWells = (boundary.wells_with_detections || [])
                      .filter((w: any) => !w.is_non_detect)
                      .map((w: any) => ({
                        lat: w.lat,
                        lon: w.lon,
                        concentration: w.concentration || boundary.max_concentration_ug_l,
                      }));
                    const belowWells = (boundary.wells_below_threshold || [])
                      .filter((w: any) => !w.is_non_detect && w.concentration > 0)
                      .map((w: any) => ({
                        lat: w.lat,
                        lon: w.lon,
                        concentration: w.concentration,
                      }));
                    const wellsWithConc = [...aboveWells, ...belowWells];

                    const isSelected = selectedCompound === compoundKey;
                    const isPancakeMode = stage === 'pancake-stack';

                    return (
                      <AnimatedStackGroup
                        key={`multi-${boundary.compound}-${idx}`}
                        targetY={stackOffsetTarget}
                        onDoubleClick={isPancakeMode ? (e: any) => {
                          e.stopPropagation();
                          onCompoundDoubleClick(compoundKey);
                        } : undefined}
                      >
                        {/* Compound label in pancake and single-plume modes */}
                        {(isPancakeMode || stage === 'single-plume') && (
                          <Text
                            position={[-8, -0.3, 0]}
                            fontSize={stage === 'single-plume' ? 0.8 : 0.6}
                            color={baseColor}
                            anchorX="right"
                            fontWeight="bold"
                          >
                            {boundary.compound.toUpperCase()}
                          </Text>
                        )}

                        {/* Clickable area for compound selection in stack mode - invisible but raycastable */}
                        {isPancakeMode && (
                          <mesh
                            position={[0, -0.5, 0]}
                            rotation={[-Math.PI / 2, 0, 0]}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              onCompoundDoubleClick(compoundKey);
                            }}
                          >
                            <circleGeometry args={[12, 32]} />
                            <meshBasicMaterial
                              transparent
                              opacity={0.001}
                              depthWrite={false}
                            />
                          </mesh>
                        )}

                        <ParticleBasedPlume3D
                          polygon={boundary.boundary}
                          wells={wellsWithConc}
                          maxConcentration={boundary.max_concentration_ug_l}
                          depthToWaterFt={effectiveDepthToWater}
                          sourceLatLon={srcLatLon}
                          baseColor={baseColor}
                          compound={boundary.compound}
                        />
                      </AnimatedStackGroup>
                    );
                  })}
              </>
            )}
          </group>
        )}

        {/* Vapor Plume - clickable */}
        {showVapor && (
          <group onDoubleClick={() => onPlumeDoubleClick(focusedPlume === 'vapor' ? null : 'vapor')}>
            <VaporPlume
              areaSqft={plumeState.vapor.area_sqft}
              depthToWaterFt={effectiveDepthToWater}
              vaporDepthFt={plumeState.vapor.depth_ft}
              maxConcUgM3={plumeState.vapor.max_conc_ug_m3}
              peakConcUgM3={peakVaporConcUgM3}
            />
          </group>
        )}

        {/* Wells - hidden in focus mode */}
        {showWells && (
          <>
            {recoveryWells.map((well, idx) => (
              <RecoveryWellMesh
                key={well.id || `recovery-${idx}`}
                well={well}
                depthToWaterFt={effectiveDepthToWater}
                aquiferThicknessFt={aquiferThicknessFt}
                sourceLatLon={srcLatLon}
              />
            ))}

            {mdpeWells.map((well, idx) => (
              <MDPEWellMesh
                key={well.id || `mdpe-${idx}`}
                well={well}
                depthToWaterFt={effectiveDepthToWater}
                sourceLatLon={srcLatLon}
              />
            ))}

            {/* Monitoring Wells - display only */}
            {monitoringWells.map((well, idx) => (
              <MonitoringWellMesh
                key={well.id || `monitoring-${idx}`}
                well={well}
                depthToWaterFt={effectiveDepthToWater}
                aquiferThicknessFt={aquiferThicknessFt}
                sourceLatLon={srcLatLon}
              />
            ))}
          </>
        )}
      </group>

      {/* Labels and indicators - hidden in focus mode */}
      {showEnvironment && (
        <>
          <AxisLabels depthToWaterFt={effectiveDepthToWater} />
          <CompassRose flowDirection={flowDirection} />
        </>
      )}
    </>
  );
}

// ============================================================================
// PLUME TYPE LABELS
// ============================================================================

const PLUME_LABELS: Record<'psh' | 'dissolved' | 'vapor', { name: string; color: string }> = {
  psh: { name: 'PSH/LNAPL', color: 'text-amber-400' },
  dissolved: { name: 'Dissolved Plume', color: 'text-cyan-400' },
  vapor: { name: 'Vapor Plume', color: 'text-purple-400' },
};

// Stage labels for breadcrumb navigation
const STAGE_LABELS: Record<InspectionStage, string> = {
  'overview': '3D Overview',
  'dissolved-focus': 'Dissolved Plumes',
  'pancake-stack': 'Compound Stack',
  'single-plume': 'Single Compound',
  'psh-focus': 'PSH Magnifier',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ThreePlume3DView(props: ThreePlume3DViewProps) {
  const orbitControlsRef = useRef<any>(null);

  // PSH wells: use pre-computed snapshot data when available (instant ~30ms mesh gen),
  // otherwise fall back to usePSHData API call (1-3 sec per slider tick)
  const { wells: apiPshWells, loading: pshLoading } = usePSHData(
    props.pshWellsFromSnapshot ? null : (props.siteId || null),  // Skip API when snapshot available
    props.pshTargetDate,
  );
  const rawPshWells = props.pshWellsFromSnapshot || apiPshWells;
  // Memoize by content so PSHVoxelCube doesn't recompute 100K voxels on every render
  const pshWellsKey = rawPshWells.length > 0 ? JSON.stringify(rawPshWells) : '';
  const pshWells = useMemo(() => rawPshWells, [pshWellsKey]);

  // Derive effective depth-to-water from PSH well gauging data (for OrbitControls target)
  const outerEffectiveDTW = useMemo(() => {
    const wDTW = pshWells.filter(w => w.depth_to_water_ft != null && w.depth_to_water_ft > 0);
    return wDTW.length > 0
      ? wDTW.reduce((s, w) => s + w.depth_to_water_ft!, 0) / wDTW.length
      : props.depthToWaterFt;
  }, [pshWells, props.depthToWaterFt]);

  // PSH vertical exaggeration metadata from voxel cube
  const [pshMeta, setPshMeta] = useState<PSHMetadata | null>(null);

  // PSH magnifier slider state (1× true scale → 35× max)
  const [pshVE, setPshVE] = useState(1);

  // Multi-stage inspection state (stackSeparation animation handled by AnimatedStackGroup via useFrame)
  const [inspectionState, setInspectionState] = useState<InspectionState>({
    stage: 'overview',
    focusedPlume: null,
    selectedCompound: null,
  });

  // Stage navigation handlers
  const handlePlumeDoubleClick = useCallback((plumeType: FocusedPlumeType) => {
    if (plumeType === 'dissolved') {
      // Dissolved plume: advance to dissolved-focus stage
      setInspectionState(prev => ({
        ...prev,
        stage: 'dissolved-focus',
        focusedPlume: 'dissolved',
        selectedCompound: null,
      }));
    } else if (plumeType === null) {
      // Back to overview
      setInspectionState({
        stage: 'overview',
        focusedPlume: null,
        selectedCompound: null,
      });
    } else if (plumeType === 'psh') {
      // PSH: dedicated focus mode with VE magnifier
      setInspectionState({
        stage: 'psh-focus',
        focusedPlume: 'psh',
        selectedCompound: null,
      });
    } else {
      // Vapor: simple focus mode
      setInspectionState(prev => ({
        ...prev,
        stage: 'overview',
        focusedPlume: plumeType,
        selectedCompound: null,
      }));
    }
  }, []);

  const handleDissolvedDoubleClick = useCallback(() => {
    // From dissolved-focus: expand to pancake stack
    setInspectionState(prev => {
      return {
        ...prev,
        stage: 'pancake-stack',
        focusedPlume: 'dissolved',
        selectedCompound: null,
      };
    });
  }, []);

  const handleCompoundDoubleClick = useCallback((compound: string) => {
    // From pancake stack: isolate single compound
    setInspectionState(prev => ({
      ...prev,
      stage: 'single-plume',
      selectedCompound: compound,
    }));
  }, []);

  // Breadcrumb navigation
  const goToOverview = useCallback(() => {
    setInspectionState({
      stage: 'overview',
      focusedPlume: null,
      selectedCompound: null,
    });
    setPshVE(1);  // Reset magnifier
  }, []);

  const goToDissolvedFocus = useCallback(() => {
    setInspectionState(prev => ({
      ...prev,
      stage: 'dissolved-focus',
      focusedPlume: 'dissolved',
      selectedCompound: null,
    }));
  }, []);

  const goToPancakeStack = useCallback(() => {
    setInspectionState(prev => ({
      ...prev,
      stage: 'pancake-stack',
      focusedPlume: 'dissolved',
      selectedCompound: null,
    }));
  }, []);

  const { stage, focusedPlume, selectedCompound } = inspectionState;
  const isInspecting = stage !== 'overview';

  return (
    <div className="w-full rounded-lg overflow-hidden border border-mono-700 bg-mono-950 relative">
      {/* Header */}
      <div className="bg-gradient-to-r from-mono-900 via-mono-850 to-mono-900 p-3 border-b border-mono-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
              3D Cross-Section View
            </span>
            <span className="text-xs text-mono-500">|</span>
            <span className="text-xs text-cyan-400 font-mono">
              Month {props.plumeState.time_months}
            </span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-mono-500">
            <span>Drag to rotate</span>
            <span>Scroll to zoom</span>
            <span>Double-click to drill down</span>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation - Top Right Overlay */}
      {isInspecting && (
        <div className="absolute top-16 right-4 z-10 flex flex-col gap-1">
          {/* Back to Overview */}
          <button
            onClick={goToOverview}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-mono-300 bg-mono-900/90 backdrop-blur-sm border border-mono-700 rounded-md hover:bg-mono-800 hover:text-mono-100 transition-all shadow-lg"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Overview
          </button>

          {/* Back to Dissolved Focus (visible in pancake-stack or single-plume) */}
          {(stage === 'pancake-stack' || stage === 'single-plume') && (
            <button
              onClick={goToDissolvedFocus}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-cyan-400 bg-mono-900/90 backdrop-blur-sm border border-cyan-800 rounded-md hover:bg-cyan-900/50 hover:text-cyan-300 transition-all shadow-lg"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Dissolved Group
            </button>
          )}

          {/* Back to Pancake Stack (visible only in single-plume) */}
          {stage === 'single-plume' && (
            <button
              onClick={goToPancakeStack}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-mono-900/90 backdrop-blur-sm border border-mono-700 rounded-md hover:bg-mono-800 transition-all shadow-lg"
              style={{ color: COMPOUND_COLORS[selectedCompound || 'benzene']?.hex || '#06b6d4' }}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Compound Stack
            </button>
          )}
        </div>
      )}

      {/* Inspection Mode Indicator */}
      {isInspecting && (
        <div className="bg-mono-900/95 backdrop-blur-sm px-4 py-2 border-b border-mono-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <div className="flex items-center gap-2 text-sm font-medium text-mono-200">
              {/* Breadcrumb path */}
              <span className="text-mono-500">3D</span>
              <span className="text-mono-600">/</span>
              {stage === 'dissolved-focus' && (
                <span className="text-cyan-400">Dissolved Plumes</span>
              )}
              {stage === 'pancake-stack' && (
                <>
                  <span className="text-cyan-400/60">Dissolved</span>
                  <span className="text-mono-600">/</span>
                  <span className="text-cyan-400">Compound Stack</span>
                </>
              )}
              {stage === 'single-plume' && selectedCompound && (
                <>
                  <span className="text-cyan-400/60">Dissolved</span>
                  <span className="text-mono-600">/</span>
                  <span className="text-mono-400">Stack</span>
                  <span className="text-mono-600">/</span>
                  <span style={{ color: COMPOUND_COLORS[selectedCompound]?.hex || '#06b6d4' }}>
                    {COMPOUND_COLORS[selectedCompound]?.name || selectedCompound.toUpperCase()}
                  </span>
                </>
              )}
              {focusedPlume === 'psh' && (
                <span className="text-amber-400">PSH/LNAPL</span>
              )}
              {focusedPlume === 'vapor' && (
                <span className="text-purple-400">Vapor Plume</span>
              )}
            </div>
          </div>
          <div className="text-[10px] text-mono-500">
            {stage === 'dissolved-focus' && 'Double-click to expand compound stack'}
            {stage === 'pancake-stack' && 'Double-click a compound to isolate'}
            {stage === 'single-plume' && 'Use buttons above to navigate back'}
            {stage === 'psh-focus' && 'Use slider to magnify vertical topology'}
          </div>
        </div>
      )}

      {/* 3D Canvas */}
      <div style={{ height: '650px', background: '#0a0a0f' }}>
        <Canvas
          frameloop="demand"
          camera={{
            position: [15, 10, 15],
            fov: 50,
            near: 0.1,
            far: 1000,
          }}
        >
          <Scene
            {...props}
            inspectionState={inspectionState}
            onPlumeDoubleClick={handlePlumeDoubleClick}
            onDissolvedDoubleClick={handleDissolvedDoubleClick}
            onCompoundDoubleClick={handleCompoundDoubleClick}
            orbitControlsRef={orbitControlsRef}
            pshWells={pshWells}
            onPshMetadata={setPshMeta}
            pshVE={pshVE}
          />
          <OrbitControls
            ref={orbitControlsRef}
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minDistance={2}
            maxDistance={50}
            target={[0, -outerEffectiveDTW * SCALE / 2, 0]}
            makeDefault
          />
        </Canvas>

        {/* PSH Magnifier Slider — visible only in psh-focus stage */}
        {stage === 'psh-focus' && (
          <div className="absolute bottom-20 left-4 z-10 bg-mono-900/90 backdrop-blur-sm border border-amber-800/50 rounded-lg p-3 shadow-lg" style={{ width: '220px' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Magnifier</span>
              <span className="text-xs font-mono text-amber-300">{pshVE}&times; VE</span>
            </div>
            <input
              type="range"
              min={1}
              max={35}
              step={1}
              value={pshVE}
              onChange={(e) => setPshVE(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-[9px] text-mono-500 mt-1">
              <span>1&times; True</span>
              <span>35&times; Max</span>
            </div>
          </div>
        )}
      </div>

      {/* Legend with concentration info */}
      <div className="bg-gradient-to-r from-mono-900 via-mono-850 to-mono-900 p-3 border-t border-mono-700">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded flex overflow-hidden">
              <div className="w-1/3 h-full" style={{ background: '#b45309' }}></div>
              <div className="w-1/3 h-full" style={{ background: COLORS.psh.core }}></div>
              <div className="w-1/3 h-full" style={{ background: COLORS.psh.outer }}></div>
            </div>
            <span className="text-amber-400">PSH</span>
            {pshMeta ? (
              <span className="text-mono-500">{pshMeta.maxThicknessFt.toFixed(2)} ft max | {Math.round(pshMeta.verticalExaggeration)}x VE</span>
            ) : (
              <span className="text-mono-500">{props.plumeState.psh?.mass_lbs?.toLocaleString() ?? 'N/A'} lbs</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded flex overflow-hidden">
              <div className="w-1/3 h-full" style={{ background: '#0369a1' }}></div>
              <div className="w-1/3 h-full" style={{ background: COLORS.dissolved.core }}></div>
              <div className="w-1/3 h-full" style={{ background: COLORS.dissolved.outer }}></div>
            </div>
            <span className="text-cyan-400">Dissolved</span>
            <span className="text-mono-500">{(props.plumeState.dissolved.max_conc_ug_l / 1000).toFixed(3)} mg/L</span>
          </div>
          <div className="flex items-center gap-2 border-l border-mono-700 pl-4">
            <div className="w-4 h-4 rounded" style={{ background: COLORS.waterTable }}></div>
            <span className="text-blue-300">Water Table</span>
            <span className="text-mono-500">({outerEffectiveDTW.toFixed(1)} ft bgs)</span>
          </div>
          {/* Well indicators */}
          {(props.recoveryWells?.length || 0) > 0 && (
            <div className="flex items-center gap-2 border-l border-mono-700 pl-4">
              <div className="w-4 h-4 rounded" style={{ background: COLORS.recoveryWell }}></div>
              <span className="text-mono-300">Recovery</span>
              <span className="text-mono-500">({props.recoveryWells?.length})</span>
            </div>
          )}
          {(props.mdpeWells?.length || 0) > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ background: COLORS.mdpeWell }}></div>
              <span className="text-mono-300">MDPE</span>
              <span className="text-mono-500">({props.mdpeWells?.length})</span>
            </div>
          )}
          {(props.monitoringWells?.length || 0) > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ background: '#22d3ee' }}></div>
              <span className="text-cyan-300">Monitoring</span>
              <span className="text-mono-500">({props.monitoringWells?.length})</span>
            </div>
          )}
        </div>
        <div className="text-[10px] text-mono-600 mt-2">
          Dark core = high concentration | Light outer = low concentration | Particles follow gradient</div>
      </div>
    </div>
  );
}

export default ThreePlume3DView;
